/* ==========================================================================
   Theme Enhancements PRO — advanced interactions
   - Scroll progress
   - Stagger-in grids
   - Toast on add-to-cart
   - Wishlist heart (localStorage only, no server impact)
   - Parallax on banner media
   - Page transition fade
   - Ripple on buttons
   - Quantity buttons polish
   - Keyboard shortcut: "/" focuses search
   - Image preload on card hover (snappier PDP nav)
   Non-destructive: observes existing DOM, never rewrites it.
   ========================================================================== */
(function () {
  'use strict';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isTouch = window.matchMedia('(hover: none)').matches;

  /* ------------------------------------------------------------------ */
  /* 1. Scroll progress bar                                              */
  /* ------------------------------------------------------------------ */
  function scrollProgress() {
    var bar = document.createElement('div');
    bar.className = 'te-progress';
    document.body.appendChild(bar);
    var ticking = false;
    function update() {
      ticking = false;
      var h = document.documentElement;
      var max = h.scrollHeight - h.clientHeight;
      var pct = max > 0 ? (h.scrollTop / max) * 100 : 0;
      bar.style.width = pct.toFixed(2) + '%';
    }
    document.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------ */
  /* 2. Stagger-in for grids (featured-collection, product grids, etc.) */
  /* ------------------------------------------------------------------ */
  function staggerGrids() {
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    var grids = document.querySelectorAll(
      '.product-grid, .grid.product-grid, .collection__grid, ' +
      '.grid--2-col-tablet-down, .slider--mobile, .slider-grid, ' +
      '.grid--4-col-desktop, .grid--3-col-tablet'
    );
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('te-stagger', 'is-visible');
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -5% 0px', threshold: 0.05 });
    grids.forEach(function (g) {
      // Only add stagger markup on grids with children
      if (g.children && g.children.length > 0 && g.children.length <= 16) {
        g.classList.add('te-stagger');
        io.observe(g);
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* 3. Toast notifications                                              */
  /* ------------------------------------------------------------------ */
  function ensureToastHost() {
    var host = document.querySelector('.te-toast-host');
    if (host) return host;
    host = document.createElement('div');
    host.className = 'te-toast-host';
    host.setAttribute('role', 'status');
    host.setAttribute('aria-live', 'polite');
    document.body.appendChild(host);
    return host;
  }
  function showToast(message, type) {
    var host = ensureToastHost();
    var t = document.createElement('div');
    t.className = 'te-toast te-toast--' + (type || 'info');
    var icon = '';
    if (type === 'success') {
      icon = '<span class="te-check-circle"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg></span>';
    }
    t.innerHTML = icon + '<span>' + message + '</span>';
    host.appendChild(t);
    setTimeout(function () { t.classList.add('is-leaving'); }, 2800);
    setTimeout(function () { t.remove(); }, 3200);
  }

  /* Hook: detect add-to-cart via fetch override (safe — does not break forms) */
  function hookAddToCartToast() {
    if (!window.fetch) return;
    var _fetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isCartAdd = url.indexOf('/cart/add') !== -1;
      return _fetch.apply(this, arguments).then(function (res) {
        if (isCartAdd && res && res.ok) {
          try { showToast('Produs adăugat în coș', 'success'); } catch (e) {}
        }
        return res;
      });
    };
  }

  /* ------------------------------------------------------------------ */
  /* 4. Wishlist heart on product cards (localStorage only)              */
  /* ------------------------------------------------------------------ */
  var WISH_KEY = 'te_wishlist_v1';
  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(WISH_KEY) || '[]'); }
    catch (e) { return []; }
  }
  function setWishlist(list) {
    try { localStorage.setItem(WISH_KEY, JSON.stringify(list)); } catch (e) {}
  }
  function toggleWish(id) {
    var list = getWishlist();
    var i = list.indexOf(id);
    if (i === -1) { list.push(id); setWishlist(list); return true; }
    list.splice(i, 1); setWishlist(list); return false;
  }
  function cardId(card) {
    var a = card.querySelector('a[href*="/products/"]');
    if (!a) return null;
    var m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }
  function heartSVG(filled) {
    return '<svg viewBox="0 0 24 24" fill="' + (filled ? 'currentColor' : 'none') + '" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
           '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';
  }
  function enhanceCards() {
    var cards = document.querySelectorAll('.card-wrapper');
    var wishlist = getWishlist();
    cards.forEach(function (card) {
      if (card.querySelector('.te-fav')) return;
      var id = cardId(card);
      if (!id) return;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'te-fav is-ready';
      btn.setAttribute('aria-label', 'Adaugă la favorite');
      var active = wishlist.indexOf(id) !== -1;
      if (active) btn.classList.add('is-active');
      btn.innerHTML = heartSVG(active);
      btn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var nowActive = toggleWish(id);
        btn.classList.toggle('is-active', nowActive);
        btn.innerHTML = heartSVG(nowActive);
        btn.animate(
          [{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }],
          { duration: 300, easing: 'cubic-bezier(.34,1.56,.64,1)' }
        );
        showToast(nowActive ? 'Adăugat la favorite' : 'Scos de la favorite', 'info');
      });
      // Ensure parent is positioned
      var media = card.querySelector('.card__media, .card .media, .card--media');
      if (media && getComputedStyle(media).position === 'static') {
        media.style.position = 'relative';
      }
      (media || card).appendChild(btn);
    });
  }

  /* ------------------------------------------------------------------ */
  /* 5. Banner parallax                                                  */
  /* ------------------------------------------------------------------ */
  function parallax() {
    if (reduceMotion || isTouch) return;
    var targets = document.querySelectorAll('.banner__media img, .banner__media video');
    if (!targets.length) return;
    var ticking = false;
    function update() {
      ticking = false;
      targets.forEach(function (t) {
        var r = t.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        var vh = window.innerHeight;
        var progress = (r.top + r.height / 2 - vh / 2) / vh; // -1..1 ish
        t.style.transform = 'translateY(' + (progress * -12).toFixed(1) + 'px) scale(1.05)';
      });
    }
    document.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------ */
  /* 6. Page transition fade (subtle)                                    */
  /* ------------------------------------------------------------------ */
  function pageTransition() {
    if (reduceMotion) return;
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a');
      if (!a) return;
      var href = a.getAttribute('href') || '';
      if (!href || href[0] === '#' || href.indexOf('javascript:') === 0) return;
      if (a.target === '_blank') return;
      if (a.hasAttribute('download')) return;
      var sameOrigin = a.host === location.host;
      if (!sameOrigin) return;
      // Skip form-like interactions
      if (a.closest('[data-type="cart-drawer"], cart-drawer, details')) return;
      document.documentElement.classList.add('te-leaving');
      setTimeout(function () {
        document.documentElement.classList.remove('te-leaving');
      }, 800);
    });
  }

  /* ------------------------------------------------------------------ */
  /* 7. Ripple on primary buttons                                         */
  /* ------------------------------------------------------------------ */
  function ripple() {
    if (reduceMotion) return;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.button, .product-form__submit, .shopify-payment-button__button');
      if (!btn) return;
      var r = btn.getBoundingClientRect();
      var d = Math.max(r.width, r.height);
      var el = document.createElement('span');
      el.className = 'te-ripple';
      el.style.width = el.style.height = d + 'px';
      el.style.left = (e.clientX - r.left - d / 2) + 'px';
      el.style.top = (e.clientY - r.top - d / 2) + 'px';
      var prev = getComputedStyle(btn).position;
      if (prev === 'static') btn.style.position = 'relative';
      btn.style.overflow = 'hidden';
      btn.appendChild(el);
      setTimeout(function () { el.remove(); }, 700);
    });
  }

  /* ------------------------------------------------------------------ */
  /* 8. Keyboard shortcut: "/" focuses search                            */
  /* ------------------------------------------------------------------ */
  function keyboardShortcuts() {
    document.addEventListener('keydown', function (e) {
      if (e.key !== '/') return;
      var tag = (e.target && e.target.tagName) || '';
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag)) return;
      if (e.target && e.target.isContentEditable) return;
      var input = document.querySelector('input[type="search"]');
      if (!input) return;
      e.preventDefault();
      var modalDetails = input.closest('details');
      if (modalDetails && !modalDetails.open) modalDetails.open = true;
      setTimeout(function () { input.focus(); input.select(); }, 50);
    });
  }

  /* ------------------------------------------------------------------ */
  /* 9. Preload product image on card hover (snappier PDP navigation)    */
  /* ------------------------------------------------------------------ */
  function preloadOnHover() {
    if (isTouch) return;
    var cards = document.querySelectorAll('.card-wrapper a[href*="/products/"]');
    cards.forEach(function (a) {
      var done = false;
      a.addEventListener('mouseenter', function () {
        if (done) return; done = true;
        var l = document.createElement('link');
        l.rel = 'prefetch';
        l.href = a.href;
        document.head.appendChild(l);
      }, { once: true, passive: true });
    });
  }

  /* ------------------------------------------------------------------ */
  /* 10. Announcement bar — pause on hover (if marquee-ish)              */
  /* ------------------------------------------------------------------ */
  function announcementBar() {
    var bar = document.querySelector('.announcement-bar');
    if (!bar) return;
    bar.addEventListener('mouseenter', function () { bar.style.animationPlayState = 'paused'; });
    bar.addEventListener('mouseleave', function () { bar.style.animationPlayState = 'running'; });
  }

  /* ------------------------------------------------------------------ */
  /* 11. Lazy iframe activation on scroll near viewport                  */
  /* ------------------------------------------------------------------ */
  function lazyIframes() {
    if (!('IntersectionObserver' in window)) return;
    var iframes = document.querySelectorAll('iframe[data-src]');
    if (!iframes.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          var f = e.target;
          f.src = f.getAttribute('data-src');
          io.unobserve(f);
        }
      });
    });
    iframes.forEach(function (f) { io.observe(f); });
  }

  /* ------------------------------------------------------------------ */
  /* 12. Rebind on Shopify section load (theme editor)                   */
  /* ------------------------------------------------------------------ */
  function bindShopify() {
    document.addEventListener('shopify:section:load', function () {
      enhanceCards();
      staggerGrids();
      announcementBar();
      parallax();
    });
  }

  /* ------------------------------------------------------------------ */
  /* 13. Smarter mobile menu — close on outside click/swipe              */
  /* ------------------------------------------------------------------ */
  function mobileMenuPolish() {
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('details[open]').forEach(function (d) {
        if (d.closest('.header-wrapper, header')) d.open = false;
      });
    });
  }

  function init() {
    scrollProgress();
    staggerGrids();
    hookAddToCartToast();
    enhanceCards();
    parallax();
    pageTransition();
    ripple();
    keyboardShortcuts();
    preloadOnHover();
    announcementBar();
    lazyIframes();
    bindShopify();
    mobileMenuPolish();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
